'use client';

// module imports
import { Check, MoreHorizontal, Star, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// file imports
import { getFeedbackReportDetails } from '@/redux/slices/feedbackDetailSlice';
import type { AppDispatch, RootState } from '@/redux/store';

interface FeedbackReportCardProps {
  comment: string;
  authorName: string;
  authorEmail?: string;
  submissionId?: string;
  slug: string;
  isHighlighted?: boolean;
  onMoreClick?: () => void;
  status?: 'pending' | 'approved' | 'rejected';
  onStatusChange?: (submissionId: string, status: 'approved' | 'rejected') => void;
  reportData?: {
    consentToShare?: boolean;
    consentToShowName?: boolean;
    actionable?: { label: string; value: number };
    engaging?: { label: string; value: number };
    interactive?: { label: string; value: number };
    inspiring?: { label: string; value: number };
    relevant?: { label: string; value: number };
    sessionValue?: { label?: string; value?: boolean | string };
    immediateApplication?: { label?: string; value?: boolean | string };
    engagementLevel?: { label?: string; value?: boolean | string };
    topicRelevance?: { label?: string; value?: boolean | string };
    npsScore?: { label?: string; value: number };
    memorableInsight?: string;
    speakerStyle?: string;
  };
}

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const StarRatingItem = ({ value, label }: { value: number; label: string }) => {
  return (
    <div className="flex items-center space-x-3">
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((rating) => (
          <svg key={rating} className={`w-4 h-4 ${rating <= value ? 'text-yellow-400' : 'text-gray-300'}`} fill={rating <= value ? 'currentColor' : 'none'} stroke={rating <= value ? 'currentColor' : 'currentColor'} strokeWidth={rating <= value ? 0 : 1} viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
      <span className="text-sm text-gray-700">{label}</span>
    </div>
  );
};

const FeedbackOptionItem = ({ value, label }: { value: boolean; label: string }) => {
  return (
    <div className="flex items-center space-x-3">
      <span className="text-lg">{value ? '👍' : '👎'}</span>
      <span className="text-sm text-gray-700">{label}</span>
    </div>
  );
};

const DropdownMenu = ({ isOpen, onClose, onViewData }: { isOpen: boolean; onClose: () => void; onViewData: () => void }) => {
  if (!isOpen) return null;

  return (
    <div className="absolute top-8 right-0 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-10 min-w-[180px]">
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onViewData();
          onClose();
        }}
        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
      >
        View Feedback Data
      </button>
    </div>
  );
};

const FeedbackReportDataModal = ({ isOpen, onClose, authorName, authorEmail, reportData, loading = false, error = null }: { isOpen: boolean; onClose: () => void; authorName: string; authorEmail?: string; reportData?: FeedbackReportCardProps['reportData']; loading?: boolean; error?: string | null }) => {
  // Prevent background scroll when popup is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* 🔹 Overlay */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      {/* 🔹 Modal Content */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-gray-200 z-[60]" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{authorName}</h3>
            {authorEmail && <p className="text-sm text-gray-600">{authorEmail}</p>}
          </div>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
            type="button"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading feedback report data...</span>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-500 mb-2">⚠️</div>
              <p className="text-red-600 text-sm mb-4">Failed to load feedback report data</p>
              <p className="text-gray-500 text-xs">{error}</p>
            </div>
          ) : (
            <>
              {/* Consent Options */}
              {(reportData?.consentToShare !== undefined || reportData?.consentToShowName !== undefined) && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700">Consent</h4>
                  <div className="space-y-2">
                    {reportData?.consentToShare !== undefined && <FeedbackOptionItem value={reportData.consentToShare} label="Consent to share feedback" />}
                    {reportData?.consentToShowName !== undefined && <FeedbackOptionItem value={reportData.consentToShowName} label="Consent to show name on feedback" />}
                  </div>
                </div>
              )}

              {/* Star-based Rating Options */}
              {(reportData?.actionable || reportData?.engaging || reportData?.interactive || reportData?.inspiring || reportData?.relevant) && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700">Session Ratings</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {reportData?.actionable && <StarRatingItem value={reportData.actionable.value} label={reportData.actionable.label} />}
                    {reportData?.engaging && <StarRatingItem value={reportData.engaging.value} label={reportData.engaging.label} />}
                    {reportData?.interactive && <StarRatingItem value={reportData.interactive.value} label={reportData.interactive.label} />}
                    {reportData?.inspiring && <StarRatingItem value={reportData.inspiring.value} label={reportData.inspiring.label} />}
                    {reportData?.relevant && <StarRatingItem value={reportData.relevant.value} label={reportData.relevant.label} />}
                  </div>
                </div>
              )}

              {/* New Metrics */}
              {(reportData?.sessionValue || reportData?.immediateApplication || reportData?.engagementLevel || reportData?.topicRelevance) && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700">Metrics</h4>
                  <div className="space-y-2">
                    {reportData?.sessionValue !== undefined && (
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-700">Session Value</span>
                        <span className="text-sm font-medium text-gray-900">{typeof reportData.sessionValue.value === 'boolean' ? (reportData.sessionValue.value ? 'Yes' : 'No') : reportData.sessionValue.value || 'N/A'}</span>
                      </div>
                    )}
                    {reportData?.immediateApplication !== undefined && (
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-700">Immediate Application</span>
                        <span className="text-sm font-medium text-gray-900">{typeof reportData.immediateApplication.value === 'boolean' ? (reportData.immediateApplication.value ? 'Yes' : 'No') : reportData.immediateApplication.value || 'N/A'}</span>
                      </div>
                    )}
                    {reportData?.engagementLevel !== undefined && (
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-700">Engagement Level</span>
                        <span className="text-sm font-medium text-gray-900">{typeof reportData.engagementLevel.value === 'boolean' ? (reportData.engagementLevel.value ? 'Yes' : 'No') : reportData.engagementLevel.value || 'N/A'}</span>
                      </div>
                    )}
                    {reportData?.topicRelevance !== undefined && (
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-700">Topic Relevance</span>
                        <span className="text-sm font-medium text-gray-900">{typeof reportData.topicRelevance.value === 'boolean' ? (reportData.topicRelevance.value ? 'Yes' : 'No') : reportData.topicRelevance.value || 'N/A'}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* NPS Score */}
              {reportData?.npsScore !== undefined && reportData.npsScore.value !== undefined && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700">Net Promoter Score</h4>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{typeof reportData.npsScore.value === 'number' ? reportData.npsScore.value.toFixed(1) : reportData.npsScore.value}</div>
                    <div className="text-xs text-gray-600 mt-1">Out of 10</div>
                  </div>
                </div>
              )}

              {/* Memorable Insight */}
              {reportData?.memorableInsight && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700">Memorable Insight</h4>
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-gray-700 italic">&ldquo;{reportData.memorableInsight}&rdquo;</p>
                  </div>
                </div>
              )}

              {/* Speaker Style */}
              {reportData?.speakerStyle && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700">Speaker Style</h4>
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-sm text-gray-700 italic">&ldquo;{reportData.speakerStyle}&rdquo;</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default function ReportFeedbackCard({ comment, authorName, authorEmail, submissionId, slug, isHighlighted = false, onMoreClick, status = 'pending', onStatusChange, reportData }: FeedbackReportCardProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch<AppDispatch>();
  const { data: reportDetails, loading: detailsLoading, error: detailsError } = useSelector((state: RootState) => state.feedbackDetail);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleMoreClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
    onMoreClick?.();
  };

  const handleViewData = async () => {
    if (!submissionId) {
      console.warn('No submissionId provided for feedback details');
      setIsModalOpen(true);
      return;
    }

    try {
      setIsModalOpen(true);
      await dispatch(getFeedbackReportDetails({ slug, submissionId: submissionId })).unwrap();
    } catch (error) {
      console.error('Error fetching feedback details:', error);
      // Still open modal even if there's an error, using fallback data
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleStatusUpdate = async (newStatus: 'approved' | 'rejected') => {
    if (!submissionId || !onStatusChange) return;

    setIsUpdatingStatus(true);
    try {
      await onStatusChange(submissionId, newStatus);
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return (
    <>
      <div className="relative bg-blue-50 rounded-2xl p-6 shadow-sm border border-blue-100">
        {isHighlighted && (
          <div className="absolute top-4 left-4 w-6 h-6 bg-gray-200 rounded-md flex items-center justify-center">
            <Star className="w-3 h-3 text-white" fill="white" />
          </div>
        )}

        {/* Opening quote */}
        <div className="absolute top-6 left-12 text-4xl text-gray-300 font-serif leading-none">&ldquo;</div>

        {/* More options menu */}
        <div className="absolute top-4 right-4" ref={dropdownRef}>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleMoreClick();
            }}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
            type="button"
          >
            <MoreHorizontal className="w-4 h-4 text-gray-600" />
          </button>

          {/* Dropdown Menu */}
          <DropdownMenu isOpen={isDropdownOpen} onClose={() => setIsDropdownOpen(false)} onViewData={handleViewData} />
        </div>

        {/* Main content */}
        <div className="pt-8 pr-8">
          <p className="text-gray-800 text-base font-medium leading-relaxed mb-4">{comment}</p>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              {/* Avatar with initials */}
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-blue-100 text-sm font-medium">{getInitials(authorName)}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-gray-800 font-medium text-sm truncate">{authorName}</p>
                {authorEmail && (
                  <p className="text-gray-500 text-xs truncate" title={authorEmail}>
                    {authorEmail}
                  </p>
                )}
              </div>
            </div>

            {/* Status and Action Buttons */}
            <div className="flex items-center space-x-2 flex-shrink-0">
              {/* Status Badge */}
              <div className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${status === 'approved' ? 'bg-green-100 text-green-800' : status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>{status === 'approved' ? 'Approved' : status === 'rejected' ? 'Rejected' : 'Pending'}</div>

              {/* Action Buttons */}
              {status === 'pending' && submissionId && onStatusChange && (
                <div className="flex items-center space-x-1">
                  <button onClick={() => handleStatusUpdate('approved')} disabled={isUpdatingStatus} className="p-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer" title="Approve">
                    <Check className="w-3 h-3" />
                  </button>
                  <button onClick={() => handleStatusUpdate('rejected')} disabled={isUpdatingStatus} className="p-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer" title="Reject">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Closing quote */}
        <div className="absolute bottom-4 right-4 text-4xl text-gray-300 font-serif leading-none">&rdquo;</div>
      </div>

      {/* Feedback Data Modal */}
      <FeedbackReportDataModal isOpen={isModalOpen} onClose={handleCloseModal} authorName={authorName} authorEmail={authorEmail} reportData={reportDetails || reportData} loading={detailsLoading} error={detailsError} />
    </>
  );
}
